import { createSlice, createSelector, createAsyncThunk, createAction, PayloadAction } from '@reduxjs/toolkit';
import { ExperimentDesignIntervention } from '../../lib/api/experimentDesignApi';

export const requestSampleRefresh = createAction<string | null>('samples/requestRefresh');
export const clearSampleRefresh = createAction<string | null>('samples/clearRefresh');

export interface Sample {
    sample_id: string | number;
    parent_sample_id?: string | number | null;
    group_id?: number | null;
    subgroup_id?: string | number | null;
    sample_number?: number | null;
    sample_source?: string | null;
    sample_type?: string | null;
    perfusion_id?: string | number | null;
    brain_region?: string | null;
    amount?: string | null;
    amount_units?: string | null;
    location?: string | null;
    box?: string | null;
    rack?: string | null;
    shelf?: string | null;
    status?: string | null;
    sample_timepoint?: string | null;
    actual_timepoint?: string | null;
    notes?: string | null;
    exists?: boolean;
    printQueued?: boolean;
    experiment_design_group?: string | null;
    top_level_experiment_design_name?: string | null;
    interventions?: ExperimentDesignIntervention[];
    [key: string]: any; // Allow additional properties
}

interface GroupRefreshState {
    shouldRefresh: boolean;
    timestamp: number | null;
}

interface SampleState {
    samples: { [key: string | number]: Sample };
    isLoading: boolean;
    error: string | null;
    filteredIds: (string | number)[];
    filterModel: { [key: string]: any };
    shouldRefresh: boolean;
    lastRefreshTimestamp: number | null;
    groupRefreshState: { [groupId: string]: GroupRefreshState };
}

interface BulkSampleActionPayload {
    actionType: 'remove' | 'update' | 'flagForPrint';
    sampleIds: (string | number)[];
    metadata?: {
        fields?: { [key: string]: any };
    };
}

const initialState: SampleState = {
    samples: {},
    isLoading: false,
    error: null,
    filteredIds: [],
    filterModel: {},
    shouldRefresh: false,
    lastRefreshTimestamp: null,
    groupRefreshState: {},
};

export const sampleSlice = createSlice({
    name: 'sample',
    initialState,
    reducers: {
        setSampleLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setSampleError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        setSamples: (state, action: PayloadAction<Sample[]>) => {
            state.samples = {};
            action.payload.forEach(sample => {
                if (sample.sample_id) {
                    state.samples[sample.sample_id] = {
                        ...sample,
                        exists: true
                    };
                }
            });
        },
        upsertSample: (state, action: PayloadAction<Sample>) => {
            const sample = action.payload;
            if (sample?.sample_id) {
                state.samples[sample.sample_id] = {
                    ...state.samples[sample.sample_id],
                    ...sample,
                    exists: true
                };
            }
        },
        upsertSamples: (state, action: PayloadAction<Sample[]>) => {
            const samples = action.payload;
            samples.forEach(sample => {
                if (sample?.sample_id) {
                    state.samples[sample.sample_id] = {
                        ...state.samples[sample.sample_id],
                        ...sample,
                        exists: true
                    };
                }
            });
        },
        removeSampleById: (state, action: PayloadAction<string | number>) => {
            const sampleId = action.payload;
            if (sampleId && state.samples[sampleId]) {
                delete state.samples[sampleId];
            }
        },
        setFilterModel: (state, action: PayloadAction<{ [key: string]: any }>) => {
            state.filterModel = action.payload;
        },
        setFilteredIds: (state, action: PayloadAction<(string | number)[]>) => {
            state.filteredIds = action.payload;
        },
        clearSamples: (state) => {
            state.samples = {};
            state.filteredIds = [];
        },
        performBulkSampleAction: (state, action: PayloadAction<BulkSampleActionPayload>) => {
            const { actionType, sampleIds, metadata = {} } = action.payload;

            switch (actionType) {
                case 'remove':
                    sampleIds.forEach(sampleId => {
                        if (state.samples[sampleId]) {
                            delete state.samples[sampleId];
                        }
                    });
                    break;

                case 'update':
                    sampleIds.forEach(sampleId => {
                        if (state.samples[sampleId]) {
                            state.samples[sampleId] = {
                                ...state.samples[sampleId],
                                ...metadata.fields
                            };
                        }
                    });
                    break;

                case 'flagForPrint':
                    sampleIds.forEach(sampleId => {
                        if (state.samples[sampleId]) {
                            state.samples[sampleId].printQueued = true;
                        }
                    });
                    break;

                default:
                    console.warn(`Unhandled bulk action type: ${actionType}`);
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(requestSampleRefresh, (state, action) => {
                const groupId = action.payload;
                if (groupId) {
                    state.groupRefreshState[groupId] = {
                        shouldRefresh: true,
                        timestamp: Date.now()
                    };
                } else {
                    state.shouldRefresh = true;
                    state.lastRefreshTimestamp = Date.now();
                }
            })
            .addCase(clearSampleRefresh, (state, action) => {
                const groupId = action.payload;
                if (groupId) {
                    if (state.groupRefreshState[groupId]) {
                        state.groupRefreshState[groupId].shouldRefresh = false;
                    }
                } else {
                    state.shouldRefresh = false;
                }
            });
    }
});

export const {
    setSampleLoading,
    setSampleError,
    setSamples,
    upsertSample,
    upsertSamples,
    removeSampleById,
    setFilterModel,
    setFilteredIds,
    performBulkSampleAction
} = sampleSlice.actions;

// Selectors
export const selectSamplesMap = (state: { samples: SampleState }) => state.samples.samples;
export const selectSamplesLoading = (state: { samples: SampleState }) => state.samples.isLoading;
export const selectSamplesError = (state: { samples: SampleState }) => state.samples.error;
const selectFilteredIds = (state: { samples: SampleState }) => state.samples.filteredIds;
const selectFilterModel = (state: { samples: SampleState }) => state.samples.filterModel;
const selectShouldRefresh = (state: { samples: SampleState }) => state.samples.shouldRefresh;
const selectLastRefreshTimestamp = (state: { samples: SampleState }) => state.samples.lastRefreshTimestamp;
const selectGroupRefreshState = (state: { samples: SampleState }, groupId: string) =>
    state.samples.groupRefreshState[groupId] || { shouldRefresh: false, timestamp: null };

const selectSampleById = (state: { samples: SampleState }, sampleId: string | number) =>
    state.samples.samples[sampleId];

export const selectAllSamples = createSelector(
    [selectSamplesMap],
    (samplesMap) => Object.values(samplesMap)
);

const selectFilteredSamples = createSelector(
    [selectSamplesMap, selectFilteredIds],
    (samplesMap, filteredIds) => filteredIds.map(id => samplesMap[id]).filter(Boolean)
);

export const checkSampleExists = (state: { samples: SampleState }, sampleId: string | number) => {
    return Boolean(state.samples.samples[sampleId]);
};

// Get required samples for a group
export const selectSamplesForGroup = createSelector(
    [
        selectSamplesMap,
        (state: { samples: SampleState }, groupSamples: any[]) => groupSamples // Second argument is the group's samples array
    ],
    (samplesMap, groupSamples) => {
        if (!groupSamples || !Array.isArray(groupSamples)) return [];

        // Get all sample IDs from the group
        const requiredIds = new Set<string | number>();
        groupSamples.forEach(sample => {
            if (sample.sample_id) requiredIds.add(sample.sample_id);
            if (sample.parent_sample_id) requiredIds.add(sample.parent_sample_id);
        });

        // Map IDs to sample data
        const result: Sample[] = [];
        requiredIds.forEach(id => {
            if (samplesMap[id]) result.push(samplesMap[id]);
        });

        return result;
    }
);

export default sampleSlice.reducer; 